import { Button, Input, Select, SelectItem } from '@nextui-org/react';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@nextui-org/modal';
import React, { useEffect, useState } from 'react';

import { ResourceInfo, SectionInfo } from '../types';
import { updateResource } from '../services/resourceService';
import { useAuth0 } from '@auth0/auth0-react';

interface EditResourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  resource: ResourceInfo | null;
  sections: SectionInfo[];
  onSaved: () => void;
}

const EditResourceModal: React.FC<EditResourceModalProps> = ({
  isOpen,
  onClose,
  resource,
  sections,
  onSaved,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [title, setTitle] = useState('');
  const [section, setSection] = useState('');
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isLink = resource?.resourceType === 'link';

  useEffect(() => {
    if (resource) {
      setTitle(resource.title);
      setSection(resource.section);
      setUrl(resource.url || '');
    }
  }, [resource]);

  const handleSave = async () => {
    if (!resource || !title || !section) return;

    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const data: Record<string, unknown> = { title, section };
      if (isLink) data.url = url;
      await updateResource(token, resource._id, data);
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update resource');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1 pb-2">
          <h3 className="text-lg font-bold text-slate-800">Edit Resource</h3>
        </ModalHeader>
        <ModalBody className="gap-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          <Input
            label="Title"
            variant="bordered"
            radius="sm"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            classNames={{ label: 'text-slate-500 text-xs' }}
          />
          {isLink && (
            <Input
              label="URL"
              variant="bordered"
              radius="sm"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              classNames={{ label: 'text-slate-500 text-xs' }}
            />
          )}
          <Select
            label="Section"
            variant="bordered"
            radius="sm"
            selectedKeys={section ? [section] : []}
            onSelectionChange={(keys) => {
              const val = Array.from(keys)[0] as string;
              setSection(val);
            }}
            classNames={{ label: 'text-slate-500 text-xs' }}
          >
            {sections.map((s) => (
              <SelectItem key={s.name} value={s.name}>
                {s.name}
              </SelectItem>
            ))}
          </Select>
          <div className="text-xs text-slate-400">
            {isLink
              ? `Type: External Link`
              : `File: ${resource?.filename} (.${resource?.fileType})`}
          </div>
        </ModalBody>
        <ModalFooter className="pt-2">
          <Button variant="bordered" onClick={onClose} radius="sm" className="border-slate-300 text-slate-600">
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSave}
            radius="sm"
            isLoading={isLoading}
            className="bg-airlock-purple font-medium"
          >
            Save Changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditResourceModal;
